// covered-by: frontends/carrie/src/ask-v2/transport/__tests__/createAskV2Transport.test.ts
import {
  type AskV2ChatAck,
  type AskV2ChatApi,
  type AskV2ThreadMessage,
  type AskV2TransportSnapshot,
} from "./types";

export type AskV2Transport = {
  getSnapshot: () => AskV2TransportSnapshot;
  subscribe: (
    listener: (snapshot: AskV2TransportSnapshot) => void,
  ) => () => void;
  setComposer: (text: string) => void;
  send: () => Promise<void>;
  abort: () => Promise<void>;
  retry: (clientId: string) => Promise<void>;
  beginEdit: (clientId: string) => void;
  setEditDraft: (text: string) => void;
  cancelEdit: () => void;
  submitEdit: () => Promise<void>;
};

const emptySnapshot = (): AskV2TransportSnapshot => ({
  chatId: null,
  composer: "",
  messages: [],
  inFlight: false,
  sending: false,
  aborting: false,
  editingClientId: null,
  editDraft: "",
});

function lastStreamingAssistant(
  messages: AskV2ThreadMessage[],
): AskV2ThreadMessage | undefined {
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    const message = messages[i];
    if (message?.role === "assistant" && message.status === "streaming") {
      return message;
    }
  }
  return undefined;
}

function assistantFromAck(
  clientId: string,
  assistant: AskV2ChatAck["assistantMessage"],
): AskV2ThreadMessage {
  // why: live create/send ACKs omit assistantMessage; greeting/demo include
  // it. Empty content (or a missing assistant) is a streaming turn. Content
  // means the assistant already finished (greeting).
  const streaming = assistant == null || assistant.content === "";
  return {
    clientId,
    serverId: assistant?.id,
    role: "assistant",
    content: assistant?.content ?? "",
    status: streaming ? "streaming" : "sent",
  };
}

export function createAskV2Transport(deps: {
  api: AskV2ChatApi;
  id: () => string;
}): AskV2Transport {
  let snapshot = emptySnapshot();
  let turnGeneration = 0;
  const listeners = new Set<(next: AskV2TransportSnapshot) => void>();

  function emit(next: AskV2TransportSnapshot): void {
    snapshot = next;
    listeners.forEach((listener) => listener(snapshot));
  }

  function mutate(
    updater: (current: AskV2TransportSnapshot) => AskV2TransportSnapshot,
  ): AskV2TransportSnapshot {
    emit(updater(snapshot));
    return snapshot;
  }

  function applyAck(args: {
    userClientId: string;
    chatId: string;
    userServerId: string;
    userContent: string;
    assistant?: { id: string; content: string };
    clearComposer: boolean;
    clearEdit: boolean;
  }): void {
    const assistant = assistantFromAck(deps.id(), args.assistant);
    mutate((current) => ({
      ...current,
      chatId: args.chatId,
      composer: args.clearComposer ? "" : current.composer,
      sending: false,
      inFlight: assistant.status === "streaming",
      editingClientId: args.clearEdit ? null : current.editingClientId,
      editDraft: args.clearEdit ? "" : current.editDraft,
      messages: [
        ...current.messages.map((message) =>
          message.clientId === args.userClientId
            ? {
                ...message,
                serverId: args.userServerId,
                content: args.userContent,
                status: "sent" as const,
              }
            : message,
        ),
        assistant,
      ],
    }));
  }

  async function abortInFlight(opts?: {
    includeSendingUser?: boolean;
  }): Promise<void> {
    turnGeneration += 1;
    const includeSendingUser = opts?.includeSendingUser ?? true;
    const assistant = lastStreamingAssistant(snapshot.messages);
    const chatId = snapshot.chatId;

    mutate((current) => ({ ...current, aborting: true }));
    try {
      if (chatId && assistant?.serverId) {
        await deps.api.abortMessage({
          chatId,
          messageId: assistant.serverId,
        });
      }
    } catch {
      // why: Stop / edit-last must still unstick the composer if abort fails.
    } finally {
      mutate((current) => ({
        ...current,
        aborting: false,
        inFlight: false,
        sending: includeSendingUser ? false : current.sending,
        messages: current.messages.map((message) => {
          if (
            assistant &&
            message.clientId === assistant.clientId &&
            message.status === "streaming"
          ) {
            return { ...message, status: "aborted" as const };
          }
          if (
            includeSendingUser &&
            message.role === "user" &&
            message.status === "sending"
          ) {
            return { ...message, status: "aborted" as const };
          }
          return message;
        }),
      }));
    }
  }

  async function dispatchUserTurn(args: {
    clientId: string;
    text: string;
    clearComposerOnAck: boolean;
    generation: number;
  }): Promise<void> {
    const chatId = snapshot.chatId;
    try {
      const result = chatId
        ? await deps.api.sendMessage({ chatId, message: args.text })
        : await deps.api.createChat({ message: args.text });
      if (args.generation !== turnGeneration) return;
      const nextChatId = result.chatId ?? chatId;
      if (!nextChatId) {
        throw new Error("Ask V2 send ACK missing chatId");
      }
      applyAck({
        userClientId: args.clientId,
        chatId: nextChatId,
        userServerId: result.userMessage.id,
        userContent: result.userMessage.content,
        assistant: result.assistantMessage,
        clearComposer: args.clearComposerOnAck,
        clearEdit: false,
      });
    } catch {
      if (args.generation !== turnGeneration) return;
      mutate((current) => ({
        ...current,
        sending: false,
        messages: current.messages.map((message) =>
          message.clientId === args.clientId
            ? { ...message, status: "failed" as const }
            : message,
        ),
      }));
    }
  }

  return {
    getSnapshot: () => snapshot,
    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    setComposer(text) {
      mutate((current) => ({ ...current, composer: text }));
    },
    async send() {
      const text = snapshot.composer.trim();
      if (!text || snapshot.sending || snapshot.inFlight) return;

      const clientId = deps.id();
      const generation = turnGeneration;
      mutate((current) => ({
        ...current,
        sending: true,
        messages: [
          ...current.messages,
          {
            clientId,
            role: "user",
            content: text,
            status: "sending",
          },
        ],
      }));

      await dispatchUserTurn({
        clientId,
        text,
        clearComposerOnAck: true,
        generation,
      });
    },
    abort: () => abortInFlight({ includeSendingUser: true }),
    async retry(clientId) {
      const failed = snapshot.messages.find(
        (message) =>
          message.clientId === clientId && message.status === "failed",
      );
      if (!failed || snapshot.sending || snapshot.inFlight) return;

      const generation = turnGeneration;
      mutate((current) => ({
        ...current,
        sending: true,
        messages: current.messages.map((message) =>
          message.clientId === clientId
            ? { ...message, status: "sending" as const }
            : message,
        ),
      }));

      await dispatchUserTurn({
        clientId,
        text: failed.content,
        clearComposerOnAck: snapshot.composer.trim() === failed.content,
        generation,
      });
    },
    beginEdit(clientId) {
      const message = snapshot.messages.find(
        (item) => item.clientId === clientId,
      );
      if (!message || message.role !== "user") return;
      mutate((current) => ({
        ...current,
        editingClientId: clientId,
        editDraft: message.content,
      }));
    },
    setEditDraft(text) {
      mutate((current) => ({ ...current, editDraft: text }));
    },
    cancelEdit() {
      mutate((current) => ({
        ...current,
        editingClientId: null,
        editDraft: "",
      }));
    },
    async submitEdit() {
      const editingClientId = snapshot.editingClientId;
      const content = snapshot.editDraft.trim();
      if (!editingClientId || !content || snapshot.sending) return;

      const target = snapshot.messages.find(
        (message) => message.clientId === editingClientId,
      );
      if (!target || target.role !== "user") return;

      mutate((current) => ({
        ...current,
        sending: true,
        messages: current.messages.map((message) =>
          message.clientId === editingClientId
            ? { ...message, content, status: "sending" as const }
            : message,
        ),
      }));

      if (snapshot.inFlight) {
        await abortInFlight({ includeSendingUser: false });
      }

      if (!snapshot.chatId || !target.serverId) {
        await dispatchUserTurn({
          clientId: editingClientId,
          text: content,
          clearComposerOnAck: false,
          generation: turnGeneration,
        });
        if (
          snapshot.messages.find(
            (message) => message.clientId === editingClientId,
          )?.status === "sent"
        ) {
          mutate((current) => ({
            ...current,
            editingClientId: null,
            editDraft: "",
          }));
        }
        return;
      }

      try {
        const result = await deps.api.regenerateMessage({
          chatId: snapshot.chatId,
          messageId: target.serverId,
          content,
        });
        applyAck({
          userClientId: editingClientId,
          chatId: snapshot.chatId,
          userServerId: result.userMessage.id,
          userContent: result.userMessage.content,
          assistant: result.assistantMessage,
          clearComposer: false,
          clearEdit: true,
        });
      } catch {
        mutate((current) => ({
          ...current,
          sending: false,
          messages: current.messages.map((message) =>
            message.clientId === editingClientId
              ? { ...message, status: "failed" as const }
              : message,
          ),
        }));
      }
    },
  };
}
